import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { createTournament } from '@/api/TournamentManagement/tournamentApi';
import type { CreateTournamentInput } from '@/models/tournament';
import { TournamentFormat } from '@/models/tournament';

interface AddTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

interface FormData {
  name: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: string;
  format: string;
  description: string;
}

interface FormErrors {
  [key: string]: string;
}

const AddTournamentModal: React.FC<AddTournamentModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation('tournament');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    venue: '',
    startDate: '',
    endDate: '',
    participants: '',
    format: '0',
    description: '',
  });

  const formatOptions = [
    { value: '0', label: t('League') },
    { value: '1', label: t('Knockout') },
    { value: '2', label: t('Group Stage') },
  ];

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setApiError(null);
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setApiError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('Tournament name is required');
    }

    if (!formData.venue.trim()) {
      newErrors.venue = t('Venue is required');
    }

    if (!formData.startDate) {
      newErrors.startDate = t('Start date is required');
    }

    if (!formData.endDate) {
      newErrors.endDate = t('End date is required');
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = t('End date must be after start date');
    }

    if (!formData.participants || isNaN(parseInt(formData.participants)) || parseInt(formData.participants) < 2) {
      newErrors.participants = t('At least 2 participants required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const input: CreateTournamentInput = {
        name: formData.name.trim(),
        venue: formData.venue.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        participants: parseInt(formData.participants),
        format: parseInt(formData.format) as TournamentFormat,
        description: formData.description.trim() || undefined,
      };

      const result = await createTournament(input);

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to create tournament'));
        return;
      }

      setFormData({
        name: '',
        venue: '',
        startDate: '',
        endDate: '',
        participants: '',
        format: '0',
        description: '',
      });

      onCreated?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setApiError(msg);
      console.error('Error adding tournament:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="add-tournament-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Icon name="Trophy" size={24} className="me-2 text-primary" />
              {t('Add New Tournament')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
              disabled={isSubmitting}
              data-testid="add-tournament-close-btn"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* API Error */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 me-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{apiError}</p>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t('Basic Info')}</h3>

            <Input
              label={t('Tournament Name')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={errors.name}
              required
              placeholder={t('e.g., UAE National Football Championship 2026')}
              disabled={isSubmitting}
              data-testid="add-tournament-name-input"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Description')}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                placeholder={t('Provide a brief description of the tournament...')}
                disabled={isSubmitting}
                data-testid="add-tournament-description-input"
              />
            </div>
          </div>

          <hr className="border-border" />

          {/* Configuration */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t('Configuration')}</h3>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label={t('Format')}
                value={formData.format}
                onChange={(value) => handleSelectChange('format', value)}
                options={formatOptions}
                disabled={isSubmitting}
                data-testid="add-tournament-format-select"
              />
              <Input
                label={t('Number of Participants')}
                type="number"
                name="participants"
                value={formData.participants}
                onChange={handleInputChange}
                error={errors.participants}
                required
                min="2"
                placeholder={t('e.g., 8')}
                disabled={isSubmitting}
                data-testid="add-tournament-participants-input"
              />
            </div>
          </div>

          <hr className="border-border" />

          {/* Schedule & Venue */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t('Schedule & Venue')}</h3>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('Start Date')}
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                error={errors.startDate}
                required
                disabled={isSubmitting}
                data-testid="add-tournament-start-date"
              />
              <Input
                label={t('End Date')}
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                error={errors.endDate}
                required
                disabled={isSubmitting}
                data-testid="add-tournament-end-date"
              />
            </div>

            <Input
              label={t('Venue')}
              name="venue"
              value={formData.venue}
              onChange={handleInputChange}
              error={errors.venue}
              required
              placeholder={t('e.g., Dubai Sports City')}
              disabled={isSubmitting}
              data-testid="add-tournament-venue-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              data-testid="add-tournament-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              data-testid="add-tournament-submit-btn"
            >
              {t('Create Tournament')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTournamentModal;
